import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiDownload, FiCheck, FiClock } from "react-icons/fi";
import { formatFileSize, timeAgo } from "../../../utils/fileUtils";
import "./FileVersionHistory.css";

const FileVersionHistory = ({
  roomId,
  attachmentId,
  onClose,
  onDownload,
  getVersions,
}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVersions();
  }, [attachmentId]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const versionData = await getVersions(roomId, attachmentId);
      setVersions(versionData);
    } catch (error) { /* ignored */ } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (versionId) => {
    try {
      await onDownload(roomId, versionId);
    } catch (error) { /* ignored */ }
  };

  return (
    <motion.div
      className="rfvh-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="rfvh-modal"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rfvh-header">
          <div className="rfvh-header-title">
            <FiClock size={20} />
            <h2>Version History</h2>
          </div>
          <button className="rfvh-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="rfvh-content">
          {loading ? (
            <div className="rfvh-loading">
              <div className="rfvh-spinner"></div>
              <span>Loading versions...</span>
            </div>
          ) : versions.length === 0 ? (
            <div className="rfvh-empty">
              <FiClock size={40} />
              <p>No version history available</p>
            </div>
          ) : (
            <div className="rfvh-list">
              {versions.map((version, index) => (
                <motion.div
                  key={version._id}
                  className={`rfvh-item ${version.isLatestVersion ? "rfvh-item--latest" : ""}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="rfvh-item-header">
                    <div className="rfvh-version-badge">
                      v{version.version}
                      {version.isLatestVersion && (
                        <span className="rfvh-latest-tag">
                          <FiCheck size={12} /> Latest
                        </span>
                      )}
                    </div>
                    <button
                      className="rfvh-btn-download"
                      onClick={() => handleDownload(version._id)}
                    >
                      <FiDownload size={16} />
                      Download
                    </button>
                  </div>

                  <div className="rfvh-item-content">
                    <div className="rfvh-file-info">
                      <span className="rfvh-file-name">{version.fileName}</span>
                      <span className="rfvh-file-size">
                        {formatFileSize(version.fileSize)}
                      </span>
                    </div>

                    <div className="rfvh-meta">
                      <span>Uploaded {timeAgo(version.createdAt)}</span>
                      {version.uploadedBy && (
                        <span> by {version.uploadedBy.name}</span>
                      )}
                    </div>

                    {version.versionNotes && (
                      <div className="rfvh-notes">
                        <strong>Notes:</strong> {version.versionNotes}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rfvh-footer">
          <span className="rfvh-count">
            {versions.length} version{versions.length !== 1 ? "s" : ""} total
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FileVersionHistory;
